import { NO_DESCRIPTION_AVAILABLE } from "constants/termDescription";
import PropTypes from "prop-types";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { selectTermDescriptionsById } from "slices/shredding/detail/selectors";

import classes from "./TermDescriptionHoverTooltip.module.scss";

const MARK_SELECTOR = "[data-term-id]";
const TOOLTIP_OFFSET_PX = 6;
const VIEWPORT_MARGIN_PX = 8;

const TermDescriptionHoverTooltip = ({ containerRef }) => {
  const descriptionsById = useSelector(selectTermDescriptionsById);
  const [tooltip, setTooltip] = useState(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    const container = containerRef?.current;
    if (!container) {
      return undefined;
    }

    const resolveDescription = (rawIds) => {
      const firstId = rawIds.split(",")[0];
      return descriptionsById[firstId] || NO_DESCRIPTION_AVAILABLE;
    };

    const handleOver = (event) => {
      const target = event.target.closest(MARK_SELECTOR);
      if (!target || !container.contains(target)) {
        return;
      }
      const rawIds = target.dataset.termId;
      if (!rawIds) {
        return;
      }
      setTooltip({
        description: resolveDescription(rawIds),
        anchorRect: target.getBoundingClientRect(),
      });
    };

    const handleOut = (event) => {
      const target = event.target.closest(MARK_SELECTOR);
      if (!target) {
        return;
      }
      if (target.contains(event.relatedTarget)) {
        return;
      }
      setTooltip(null);
    };

    container.addEventListener("mouseover", handleOver);
    container.addEventListener("mouseout", handleOut);
    return () => {
      container.removeEventListener("mouseover", handleOver);
      container.removeEventListener("mouseout", handleOut);
    };
  }, [containerRef, descriptionsById]);

  useLayoutEffect(() => {
    if (!tooltip || !tooltipRef.current) {
      return;
    }
    const el = tooltipRef.current;
    const tipRect = el.getBoundingClientRect();
    const { anchorRect } = tooltip;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top = anchorRect.bottom + TOOLTIP_OFFSET_PX;
    let left = anchorRect.left;

    if (left + tipRect.width > vw - VIEWPORT_MARGIN_PX) {
      left = Math.max(
        VIEWPORT_MARGIN_PX,
        vw - tipRect.width - VIEWPORT_MARGIN_PX
      );
    }

    if (top + tipRect.height > vh - VIEWPORT_MARGIN_PX) {
      top = Math.max(
        VIEWPORT_MARGIN_PX,
        anchorRect.top - tipRect.height - TOOLTIP_OFFSET_PX
      );
    }

    el.style.top = `${top + window.scrollY}px`;
    el.style.left = `${left + window.scrollX}px`;
    el.style.visibility = "visible";
  }, [tooltip]);

  if (!tooltip) {
    return null;
  }

  return createPortal(
    <div
      ref={tooltipRef}
      className={classes.tooltip}
      role="tooltip"
      style={{ visibility: "hidden", top: 0, left: 0 }}
    >
      {tooltip.description}
    </div>,
    document.body
  );
};

TermDescriptionHoverTooltip.propTypes = {
  containerRef: PropTypes.shape({
    current: PropTypes.any,
  }).isRequired,
};

export default TermDescriptionHoverTooltip;
